import { api } from "@/api";
import { MeetingRoom, MeetingRoomRent } from "@/api/meetingRooms";
import {
  MeetingRoomTimeline,
  TimelineEvent,
  TimelineRoom,
} from "@/app/components/meetingRooms/meetingRoomTimeline";
import { MeetingRoomDateControls } from "@/app/components/meetingRooms/meetingRoomDateControls";
import { CreateMeetingRoomModal } from "@/app/components/modals/meetingRoom/createMeetingRoomModal";
import { EditMeetingRoomModal } from "@/app/components/modals/meetingRoom/editMeetingRoomModal";
import { CreateMeetingRoomRentModal } from "@/app/components/modals/meetingRoom/createMeetingRoomRentModal";
import { EditMeetingRoomRentModal } from "@/app/components/modals/meetingRoom/editMeetingRoomRentModal";
import { useAppSelector } from "@/hooks/useAppSelector";
import useErrorHandling from "@/hooks/useErrorHandling";
import { Button, Tab, Tabs, useDisclosure } from "@heroui/react";
import { format } from "date-fns";
import {
  formatDateTimeLocalWall,
  parseIsoWallClock,
  wallDayUtcBounds,
} from "@/utils/meetingRoomWallClock";
import { ru } from "date-fns/locale";
import { Key, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaArrowLeftLong, FaPlus } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export function MeetingRoomsPage() {
  const [meetingRooms, setMeetingRooms] = useState<MeetingRoom[]>([]);
  const [locationIds, setLocationIds] = useState<number[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null,
  );
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
  const [meetingRoomsLoading, setMeetingRoomsLoading] = useState<boolean>(true);
  const [meetingRoomsError, setMeetingRoomsError] = useState<string | null>(
    null,
  );

  const [roomRents, setRoomRents] = useState<Record<number, MeetingRoomRent[]>>(
    {},
  );
  const [rentsErrors, setRentsErrors] = useState<Record<number, string | null>>(
    {},
  );
  const [rentsLoading, setRentsLoading] = useState<Record<number, boolean>>({});

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [activeMeetingRoom, setActiveMeetingRoom] =
    useState<MeetingRoom | null>(null);
  const [activeRent, setActiveRent] = useState<MeetingRoomRent | null>(null);
  const [draftRentRange, setDraftRentRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);

  const createDisclosure = useDisclosure();
  const editRoomDisclosure = useDisclosure();
  const createRentDisclosure = useDisclosure();
  const editRentDisclosure = useDisclosure();
  const navigate = useNavigate();

  const locations = useAppSelector((state) => state.handbooks.location);
  const renters = useAppSelector((state) => state.handbooks.renter);
  const users = useAppSelector((state) => state.handbooks.user);

  const handleError = useErrorHandling();

  const selectedDateStr = useMemo(
    () => format(currentDate, "yyyy-MM-dd"),
    [currentDate],
  );
  const { wallDayStart, wallDayEnd } = useMemo(
    () => wallDayUtcBounds(currentDate),
    [currentDate],
  );
  const formattedCurrentDate = useMemo(
    () => format(currentDate, "d MMMM yyyy", { locale: ru }),
    [currentDate],
  );

  const orientation = isMobile ? "vertical" : "horizontal";

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setIsMobile(window.innerWidth < 1280);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const loadMeetingRooms = useCallback(async () => {
    setMeetingRoomsLoading(true);
    setMeetingRoomsError(null);

    try {
      const res = await api.meetingRooms.list();
      setMeetingRooms(res.data);
    } catch (err) {
      const { errorMessage } = handleError(err as any);
      setMeetingRoomsError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setMeetingRoomsLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    loadMeetingRooms();
  }, [loadMeetingRooms, createDisclosure.isOpen]);

  useEffect(() => {
    const uniqueLocationIds = Array.from(
      new Set(meetingRooms.map((room) => room.location_id)),
    );

    const sortedIds = [...uniqueLocationIds].sort((a, b) => {
      const aName =
        locations.find((loc) => loc.id === a)?.display_name || `Локация ${a}`;
      const bName =
        locations.find((loc) => loc.id === b)?.display_name || `Локация ${b}`;
      return aName.localeCompare(bName, "ru");
    });

    setLocationIds((prev) => {
      if (
        prev.length === sortedIds.length &&
        prev.every((id, idx) => id === sortedIds[idx])
      ) {
        return prev;
      }

      return sortedIds;
    });

    setSelectedLocationId((prev) => {
      if (prev && sortedIds.includes(prev)) {
        return prev;
      }
      return sortedIds[0] ?? null;
    });
  }, [meetingRooms, locations]);

  const roomsBySelectedLocation = useMemo(() => {
    if (!selectedLocationId) return [];
    return meetingRooms.filter(
      (room) => room.location_id === selectedLocationId,
    );
  }, [meetingRooms, selectedLocationId]);

  const fetchRentsForRooms = useCallback(
    async (roomsToLoad: MeetingRoom[]) => {
      if (roomsToLoad.length === 0) return;

      setRentsLoading((prev) => {
        const next = { ...prev };
        roomsToLoad.forEach((room) => {
          next[room.id] = true;
        });
        return next;
      });

      const results = await Promise.all(
        roomsToLoad.map(async (room) => {
          try {
            const res = await api.meetingRooms.getRents(room.id);
            return {
              roomId: room.id,
              data: res.data,
              error: null,
            };
          } catch (error) {
            const { errorMessage } = handleError(error as any);
            return {
              roomId: room.id,
              data: [] as MeetingRoomRent[],
              error: errorMessage,
            };
          }
        }),
      );

      setRoomRents((prev) => {
        const next = { ...prev };
        results.forEach(({ roomId, data }) => {
          next[roomId] = data;
        });
        return next;
      });

      setRentsErrors((prev) => {
        const next = { ...prev };
        results.forEach(({ roomId, error }) => {
          next[roomId] = error;
        });
        return next;
      });

      setRentsLoading((prev) => {
        const next = { ...prev };
        roomsToLoad.forEach((room) => {
          next[room.id] = false;
        });
        return next;
      });
    },
    [handleError],
  );

  const refreshSelectedLocationRents = useCallback(() => {
    if (!selectedLocationId) return;
    const roomsToRefresh = meetingRooms.filter(
      (room) => room.location_id === selectedLocationId,
    );
    fetchRentsForRooms(roomsToRefresh);
  }, [fetchRentsForRooms, meetingRooms, selectedLocationId]);

  useEffect(() => {
    if (!selectedLocationId) return;
    fetchRentsForRooms(roomsBySelectedLocation);
  }, [selectedLocationId, roomsBySelectedLocation, fetchRentsForRooms]);

  useEffect(() => {
    setActiveMeetingRoom(null);
    setActiveRent(null);
    setDraftRentRange(null);
  }, [selectedLocationId]);

  const locationOptions = useMemo(
    () =>
      locationIds.map((id) => ({
        id,
        label:
          locations.find((loc) => loc.id === id)?.display_name ||
          `Локация ${id}`,
      })),
    [locationIds, locations],
  );

  const timelineRooms = useMemo<TimelineRoom[]>(() => {
    return roomsBySelectedLocation.map((room) => {
      const rents = roomRents[room.id] || [];
      const events = rents.reduce<TimelineEvent[]>((acc, rent) => {
        const parsedFrom = parseIsoWallClock(rent.rent_from);
        const parsedTo = parseIsoWallClock(rent.rent_to);
        if (!parsedFrom || !parsedTo) return acc;
        if (parsedFrom.dateStr !== selectedDateStr) return acc;

        const rentStart = parsedFrom.utcWallDate;
        const rentEnd = parsedTo.utcWallDate;

        const renterName =
          renters.find((renter) => renter.id === rent.renter_id)
            ?.display_name ?? "";
        const user = users.find((usr) => usr.id === rent.user_id);
        const userName = [user?.first_name, user?.last_name]
          .filter(Boolean)
          .join(" ");

        const label = [renterName, userName]
          .filter((part) => (part || "").length > 0)
          .join(": ");

        const clippedStart =
          rentStart < wallDayStart ? wallDayStart : rentStart;
        const clippedEnd = rentEnd > wallDayEnd ? wallDayEnd : rentEnd;

        acc.push({
          id: rent.id,
          start: clippedStart,
          end: clippedEnd,
          label: label || `Бронь #${rent.id}`,
          rent,
        });
        return acc;
      }, []);

      return {
        room,
        events,
        isLoading: rentsLoading[room.id],
        error: rentsErrors[room.id],
      };
    });
  }, [
    roomsBySelectedLocation,
    roomRents,
    rentsErrors,
    rentsLoading,
    renters,
    users,
    selectedDateStr,
    wallDayStart,
    wallDayEnd,
  ]);

  const handleTimelineRoomClick = useCallback(
    (room: MeetingRoom) => {
      setActiveMeetingRoom(room);
      setActiveRent(null);
      editRoomDisclosure.onOpen();
    },
    [editRoomDisclosure],
  );

  const handleTimelineEventClick = useCallback(
    (room: MeetingRoom, event: TimelineEvent) => {
      const rent =
        event.rent ||
        roomRents[room.id]?.find((storedRent) => storedRent.id === event.id) ||
        null;
      if (!rent) return;
      setActiveMeetingRoom(room);
      setActiveRent(rent);
      editRentDisclosure.onOpen();
    },
    [editRentDisclosure, roomRents],
  );

  const handleTimelineEmptyClick = useCallback(
    (room: MeetingRoom, range: { start: Date; end: Date }) => {
      setActiveMeetingRoom(room);
      setActiveRent(null);
      setDraftRentRange(range);
      createRentDisclosure.onOpen();
    },
    [createRentDisclosure],
  );

  const handleLocationTabChange = useCallback((key: Key) => {
    if (key == null) return;
    const nextId = Number(key);
    setSelectedLocationId(Number.isNaN(nextId) ? null : nextId);
  }, []);

  const prevCreateRentOpen = useRef(false);
  useEffect(() => {
    if (prevCreateRentOpen.current && !createRentDisclosure.isOpen) {
      setDraftRentRange(null);
      setActiveMeetingRoom(null);
      setActiveRent(null);
      refreshSelectedLocationRents();
    }
    prevCreateRentOpen.current = createRentDisclosure.isOpen;
  }, [createRentDisclosure.isOpen, refreshSelectedLocationRents]);

  const prevEditRentOpen = useRef(false);
  useEffect(() => {
    if (prevEditRentOpen.current && !editRentDisclosure.isOpen) {
      setActiveMeetingRoom(null);
      setActiveRent(null);
      refreshSelectedLocationRents();
    }
    prevEditRentOpen.current = editRentDisclosure.isOpen;
  }, [editRentDisclosure.isOpen, refreshSelectedLocationRents]);

  const prevEditRoomOpen = useRef(false);
  useEffect(() => {
    if (prevEditRoomOpen.current && !editRoomDisclosure.isOpen) {
      setActiveMeetingRoom(null);
      loadMeetingRooms();
    }
    prevEditRoomOpen.current = editRoomDisclosure.isOpen;
  }, [editRoomDisclosure.isOpen, loadMeetingRooms]);

  const activeLocationKey = selectedLocationId
    ? selectedLocationId.toString()
    : null;

  const isTimelineLoading =
    meetingRoomsLoading ||
    (timelineRooms.length > 0 && timelineRooms.every((room) => room.isLoading));

  return (
    <>
      <CreateMeetingRoomModal disclosure={createDisclosure} />
      <EditMeetingRoomModal
        disclosure={editRoomDisclosure}
        meetingRoom={activeMeetingRoom}
      />
      <CreateMeetingRoomRentModal
        disclosure={createRentDisclosure}
        meetingRoom={activeMeetingRoom}
        defaultValues={
          draftRentRange
            ? {
                rent_from: formatDateTimeLocalWall(draftRentRange.start),
                rent_to: formatDateTimeLocalWall(draftRentRange.end),
              }
            : undefined
        }
      />
      <EditMeetingRoomRentModal
        disclosure={editRentDisclosure}
        meetingRoom={activeMeetingRoom}
        rent={activeRent}
      />
      <div className="flex w-full flex-col gap-6 bg-background p-4 md:p-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
              <Button
                variant="flat"
                size="sm"
                startContent={<FaArrowLeftLong />}
                onClick={() => navigate(-1)}
                className="max-w-fit">
                Назад
              </Button>
              <div className="flex flex-col">
                <span className="text-xl font-semibold">Переговорки</span>
                <span className="text-sm text-foreground-500">
                  Таймлайн бронирований на {formattedCurrentDate}
                </span>
              </div>
            </div>
            <Button
              startContent={<FaPlus />}
              color="primary"
              variant="solid"
              className="max-w-fit"
              onClick={() => createDisclosure.onOpen()}>
              Создать переговорку
            </Button>
          </div>

          {activeLocationKey && locationOptions.length > 0 ? (
            <Tabs
              aria-label="Локации переговорок"
              selectedKey={activeLocationKey}
              onSelectionChange={handleLocationTabChange}
              color="primary"
              variant="bordered"
              className="w-full"
              classNames={{ tabList: "gap-2 flex-wrap md:flex-nowrap" }}>
              {locationOptions.map((option) => (
                <Tab key={option.id.toString()} title={option.label} />
              ))}
            </Tabs>
          ) : null}

          <MeetingRoomDateControls
            date={currentDate}
            onChange={setCurrentDate}
            className="flex flex-wrap items-center gap-2"
          />

          {meetingRoomsError ? (
            <div className="rounded-xl border border-danger-300 bg-danger-100/80 p-4 text-sm text-danger-700 dark:border-danger-500 dark:bg-danger-500/20 dark:text-danger-200">
              {meetingRoomsError}
            </div>
          ) : null}
        </div>

        <div className="flex flex-1 flex-col gap-4">
          {meetingRoomsLoading && meetingRooms.length === 0 ? (
            <div className="flex flex-col gap-3">
              {[0, 1, 2].map((idx) => (
                <div
                  key={idx}
                  className="h-32 w-full rounded-2xl skeleton-shimmer"
                />
              ))}
            </div>
          ) : null}

          {!meetingRoomsLoading && !selectedLocationId ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-foreground/10 bg-content2/40 p-8 text-center text-sm text-foreground-500">
              <span>У вас пока нет локаций с переговорами.</span>
              <span>Добавьте первую переговорку, чтобы увидеть таймлайн.</span>
            </div>
          ) : null}

          {!meetingRoomsLoading &&
          selectedLocationId &&
          roomsBySelectedLocation.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-foreground/10 bg-content2/40 p-8 text-center text-sm text-foreground-500">
              <span>В выбранной локации пока нет переговорок.</span>
              <span>
                Создайте новую переговорку или выберите другую локацию.
              </span>
            </div>
          ) : null}

          {selectedLocationId &&
          roomsBySelectedLocation.length > 0 &&
          !meetingRoomsLoading ? (
            <div className="flex flex-col gap-6">
              <MeetingRoomTimeline
                date={currentDate}
                rooms={timelineRooms}
                orientation={orientation}
                intervalMinutes={30}
                startHour={8}
                endHour={22}
                onRoomClick={handleTimelineRoomClick}
                onEventClick={handleTimelineEventClick}
                onEmptySlotClick={handleTimelineEmptyClick}
              />
            </div>
          ) : null}

          {!meetingRoomsLoading &&
            !isTimelineLoading &&
            timelineRooms.some((room) => room.error) && (
              <div className="flex flex-col gap-2 rounded-xl border border-warning-300 bg-warning-100/70 p-4 text-sm text-warning-700 dark:border-warning-500 dark:bg-warning-500/20 dark:text-warning-200">
                <span>
                  Не удалось загрузить брони для некоторых переговорок.
                  Попробуйте обновить страницу позднее.
                </span>
                <ul className="list-disc pl-6">
                  {timelineRooms
                    .filter((room) => room.error)
                    .map((room) => (
                      <li key={room.room.id}>{room.room.display_name}</li>
                    ))}
                </ul>
              </div>
            )}
        </div>
      </div>
    </>
  );
}
